import { z } from "zod"
import { UpdateMembershipTierDtoSchema } from "@mimber/schema"
import { RecurrenceInterval } from "@mimber/schema"

/**
 * Helper to coerce string inputs to numbers
 */
const numberFromString = z
  .union([z.number(), z.string()])
  .transform((val) =>
    typeof val === "string" && val !== "" ? Number(val) : val
  )
  .pipe(z.number())

const optionalIntervalLengthFromString = z
  .union([z.number(), z.string(), z.undefined(), z.null()])
  .transform((val) => {
    if (val === "" || val === undefined || val === null) return undefined
    return typeof val === "string" ? Number(val) : val
  })
  .pipe(
    z
      .number()
      .int("Interval length must be an integer")
      .min(1, "Interval length must be at least 1")
      .optional()
  )

const optionalPriceInCents = z
  .union([z.number(), z.string(), z.undefined(), z.null()])
  .transform((val) => {
    if (val === "" || val === undefined || val === null) return undefined
    return typeof val === "string" ? Number(val) : val
  })
  .pipe(
    z
      .number()
      .int("Price must be an integer number of cents")
      .min(0, "Price must be non-negative")
      .optional()
  )

/**
 * Form schema that extends the DTO schema with string-to-number transformations
 * for form inputs (text fields return strings that need to be converted)
 */
export const editMembershipTierSchema = UpdateMembershipTierDtoSchema.extend({
  level: numberFromString
    .pipe(
      z
        .number()
        .int("Level must be an integer")
        .min(1, "Level must be at least 1")
    )
    .optional(),
  price: optionalPriceInCents,
  intervalLength: optionalIntervalLengthFromString,
  intervalType: z.enum(RecurrenceInterval).optional(),
  description: z.preprocess(
    (val) => (val === "" ? undefined : val),
    z
      .string()
      .max(1000, "Description must be less than 1000 characters")
      .optional()
  ),
})

/**
 * Output type after transformation - this is what gets passed to onSubmit
 */
export type EditMembershipTierData = z.output<typeof editMembershipTierSchema>
